import { useCallback } from 'react';

import {
	useGenerateSuccessRedirectUrl,
	useSignSuggestedPetition as useSignSuggestedPetitionBase,
} from 'src/app/shared/hooks/signature';
import { metaPixelTracking } from 'src/app/shared/utils/metaPixel';

import type { SuggestedPetition } from '../../../../../../../../shared/api';

import { useSignatureErrorTracking } from './useSignatureErrorTracking';
import { useSuggestedPetitionSignErrorRedirect } from './useSuggestedPetitionSignErrorRedirect';

type Options = {
	parentPetitionId?: string;
	petition: SuggestedPetition;
	gridPosition: number;
};
export type SignPetitionHookResult = { status: 'success' | 'error'; redirectUrl: string };

export function useSignSuggestedPetition({
	parentPetitionId,
	petition,
	gridPosition,
}: Options): () => Promise<SignPetitionHookResult> {
	const signPetition = useSignSuggestedPetitionBase(
		{
			id: petition.node.id,
			promotionToken: petition.promotionToken,
		},
		{
			pageContext: 'petitions_show',
			promotionPlacement: 'petition_footer',
		},
	);

	const errorRedirectUrl = useSuggestedPetitionSignErrorRedirect({
		parentPetitionId,
		petition,
		gridPosition,
		sourceLocation: 'petition_footer',
	});
	const generateSuccessRedirectUrl = useGenerateSuccessRedirectUrl();

	const {
		actions: { trackSuggestedPetitionSignError },
	} = useSignatureErrorTracking();

	return useCallback(async () => {
		const petitionId = petition.node.id;

		try {
			const signResult = await signPetition();

			if (signResult.status === 'error') {
				void trackSuggestedPetitionSignError({ petitionId, reason: signResult.reason });
				return { status: 'error', redirectUrl: errorRedirectUrl };
			}
			metaPixelTracking.trackCompleteRegistration({ source: 'suggested_petition_card' });

			return {
				status: 'success',
				redirectUrl: generateSuccessRedirectUrl(signResult.redirectUrl),
			};
		} catch (error) {
			// TODO report?
			void trackSuggestedPetitionSignError({ petitionId, submitError: error });

			return { status: 'error', redirectUrl: errorRedirectUrl };
		}
	}, [signPetition, petition, errorRedirectUrl, generateSuccessRedirectUrl, trackSuggestedPetitionSignError]);
}
